import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';
import HeroSection from '@/components/HeroSection';
import SectionHeading from '@/components/SectionHeading';
import CTASection from '@/components/CTASection';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/services.json`)
      .then(res => res.json())
      .then(data => setServices(data))
      .catch(err => console.error('Error loading services:', err));
  }, []);

  return (
    <>
      <SEO
        title="Managed Security Services & Advisory - 24/7 SOC, Compliance | Gulf Digital"
        description="Professional cybersecurity services including 24/7 managed security, SOC operations, incident response, compliance advisory, and security consulting for Middle East organizations."
        url="/services"
        ogTitle="Cybersecurity Services in KSA | Managed Security | Gulf Digital"
        ogDescription="Gulf Digital delivers Cybersecurity services in KSA including 24/7 managed security, SOC operations, incident response, and compliance advisory."
        twitterTitle="Cybersecurity Services in KSA | 24/7 SOC & Advisory"
        twitterDescription="Professional Cybersecurity services in KSA covering managed SOC, incident response, and compliance advisory for Middle East organizations."
      />
      <StructuredData type="Service" data={{
        name: 'Cybersecurity Services',
        description: 'Comprehensive managed security services including 24/7 SOC, incident response, compliance advisory, and security training'
      }} />

      <HeroSection
        eyebrow="Our Services"
        title="Managed Security Services & Expert Advisory"
        subtitle="From 24/7 security monitoring to strategic advisory, our services ensure your organization stays protected and compliant with evolving regulations."
        primaryCTA={{ text: 'Get a Proposal', href: '/contact' }}
        secondaryCTA={{ text: 'View Solutions', href: '/solutions' }}
      />

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="services-grid-section">
        <div className="container-custom">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="border-2 border-warm-gray-200 hover:border-brand-maroon transition-all duration-300 shadow-soft">
                  <CardHeader>
                    <CardTitle className="text-2xl md:text-3xl">{service.title}</CardTitle>
                    <CardDescription className="text-lg">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Services List */}
                      <div>
                        <h3 className="font-bold text-lg mb-4">What We Offer</h3>
                        <ul className="space-y-3">
                          {service.services.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <Check className="w-5 h-5 text-brand-maroon flex-shrink-0 mt-0.5" />
                              <span className="text-muted-foreground">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Outcomes List */}
                      <div>
                        <h3 className="font-bold text-lg mb-4">Key Outcomes</h3>
                        <ul className="space-y-3">
                          {service.outcomes.map((outcome, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <div className="w-2 h-2 rounded-full bg-brand-maroon flex-shrink-0 mt-2" />
                              <span className="text-muted-foreground">{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-warm-gray-200">
                      <Button asChild className="bg-brand-maroon hover:bg-brand-maroon-dark">
                        <Link to="/contact">
                          Request More Information
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Approach */}
      <section className="py-20 bg-warm-gray-50" data-testid="approach-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Approach"
            title="How We Deliver Excellence"
            titleColor='foreground'
            description="Our proven methodology ensures successful security outcomes for every client."
          />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Assessment', description: 'Understand your current security posture and requirements' },
              { step: '02', title: 'Strategy', description: 'Develop a tailored security roadmap aligned with business goals' },
              { step: '03', title: 'Implementation', description: 'Deploy solutions with minimal disruption to operations' },
              { step: '04', title: 'Optimization', description: 'Continuous improvement and adaptation to emerging threats' }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-6xl font-bold text-black/50 mb-4">{phase.step}</div>
                <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                <p className="text-muted-foreground">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Why Choose Us"
            title="Trusted by Leading Organizations"
            titleColor='foreground'
            description="Our managed services deliver measurable security and business value."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: '24/7 Monitoring', description: 'Round-the-clock security operations center with expert analysts' },
              { title: 'Rapid Response', description: 'Average incident response time of less than 15 minutes' },
              { title: 'Regional Expertise', description: 'Deep understanding of Middle East compliance and threat landscape' },
              { title: 'Proven Track Record', description: 'Successfully protected 500+ organizations across the region' },
              { title: 'Cost Efficient', description: 'Reduce security costs by up to 40% compared to in-house teams' },
              { title: 'Flexible Engagement', description: 'Tailored service packages to match your needs and budget' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl bg-warm-gray-50 hover:shadow-soft transition-shadow"
              >
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Let's Discuss Your Security Needs"
        description="Schedule a consultation with our security experts to explore how our services can protect your organization."
      />
    </>
  );
};

export default Services;